import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Bot, Sparkles, Calendar, User } from 'lucide-react';

const GROQ_API_KEY = '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';

const SYSTEM_PROMPT = `You are the American Prime LLC AI Assistant. Be helpful, friendly and answer any question.

Company: American Prime LLC
Website: americanprimellc.com
Phone: +1 (203) 589-9774
Email: info@americanprimellc.com
Founder & CEO: Naveen Dadi - dadi@americanprimellc.com (he is also the Founder & CEO of CredX USA, a second American company he started)
Book a call: https://calendly.com/americanprimeforyou

Services: AI Automation, Lead Generation, Social Media Management, Website Design, Branding
Pricing: Starter from $500/month, Growth from $1500/month, Enterprise custom pricing

Answer ANY question the user asks. Be concise (2-3 sentences). Never give the same generic response.`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{
    id: 1, role: 'bot',
    message: "Hello! 👋 I'm the American Prime AI Assistant. I can help with AI automation, pricing, services, and booking a strategy call. How can I help you today?",
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const quickReplies = [
    { label: 'AI Services', icon: Bot },
    { label: 'Pricing', icon: Sparkles },
    { label: 'Book a Call', icon: Calendar },
    { label: 'Talk to Human', icon: User }
  ];

  const handleSendMessage = async (messageText = inputValue) => {
    const text = typeof messageText === 'string' ? messageText.trim() : inputValue.trim();
    if (!text) return;

    const userMsg = { id: Date.now(), role: 'user', message: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${GROQ_API_KEY}`
        },
        body: JSON.stringify({
          model: 'llama-3.1-8b-instant',
          max_tokens: 200,
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            { role: 'user', content: text }
          ]
        })
      });

      const data = await res.json();
      console.log('Groq response:', data);
      const reply = data.choices?.[0]?.message?.content || "Thanks for reaching out! Call us at +1 (203) 589-9774 or email info@americanprimellc.com.";
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'bot', message: reply, timestamp: new Date() }]);
    } catch (err) {
      console.error('Groq error:', err);
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'bot', message: "Thanks for reaching out! Call us at +1 (203) 589-9774 or email info@americanprimellc.com.", timestamp: new Date() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = (label) => {
    const map = {
      'AI Services': 'What AI automation services do you offer?',
      'Pricing': 'What is the pricing for your services?',
      'Book a Call': 'I would like to book a strategy call',
      'Talk to Human': 'I would like to speak with someone from your team'
    };
    handleSendMessage(map[label] || label);
  };

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-24 right-6 z-50 group"
          style={{ background: '#4F46E5', color: '#FFFFFF', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(79,70,229,0.4)', border: 'none', cursor: 'pointer', transition: 'transform 0.3s ease' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          aria-label="AI Assistant">
          <Bot size={28} />
          <span className="absolute right-full mr-3 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
            style={{ background: '#4F46E5', color: '#FFFFFF', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>AI Assistant</span>
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col"
          style={{ width: '400px', maxWidth: 'calc(100vw - 48px)', height: '600px', maxHeight: 'calc(100vh - 48px)', background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

          {/* Header */}
          <div className="flex items-center justify-between p-4" style={{ background: '#4F46E5' }}>
            <div className="flex items-center space-x-3">
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
                <Bot size={20} />
              </div>
              <div>
                <div className="font-semibold text-white">AI Assistant</div>
                <div className="text-xs text-white/70">We typically reply instantly</div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={{ color: '#FFFFFF', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ background: 'var(--bg-secondary)' }}>
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[80%] p-3 rounded-lg text-sm" style={{ background: msg.role === 'user' ? '#4F46E5' : 'var(--bg-primary)', color: msg.role === 'user' ? '#FFFFFF' : 'var(--text-primary)', border: msg.role === 'bot' ? '1px solid var(--border-subtle)' : 'none', whiteSpace: 'pre-wrap' }}>
                  {msg.message}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="p-3 rounded-lg flex items-center space-x-2" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)' }}>
                  <Loader2 className="animate-spin" size={16} style={{ color: '#4F46E5' }} />
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2" style={{ background: 'var(--bg-secondary)' }}>
              <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Quick replies:</div>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <button key={idx} onClick={() => handleQuickReply(item.label)}
                      className="text-xs px-3 py-2 rounded-full flex items-center gap-1"
                      style={{ background: 'rgba(79,70,229,0.1)', color: '#4F46E5', border: '1px solid #4F46E5', cursor: 'pointer' }}>
                      <Icon size={12} />{item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Book Call CTA */}
          <div className="px-4 pb-2" style={{ background: 'var(--bg-secondary)' }}>
            <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 py-2 rounded"
              style={{ background: 'var(--brand-primary)', color: '#000000', textDecoration: 'none', fontWeight: '500' }}>
              <Calendar size={16} />
              <span className="text-sm">Book a Free Strategy Call</span>
            </a>
          </div>

          {/* Input */}
          <div className="p-4" style={{ background: 'var(--bg-primary)', borderTop: '1px solid var(--border-subtle)' }}>
            <div className="flex space-x-2">
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 rounded text-sm"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', outline: 'none' }} />
              <button onClick={handleSendMessage} disabled={!inputValue.trim()}
                className="p-2 rounded"
                style={{ background: inputValue.trim() ? '#4F46E5' : 'var(--bg-secondary)', color: inputValue.trim() ? '#FFFFFF' : 'var(--text-muted)', border: 'none', cursor: inputValue.trim() ? 'pointer' : 'not-allowed' }}>
                <Send size={20} />
              </button>
            </div>
          </div>

        </div>
      )}
    </>
  );
};

export default ChatBot;
